import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Upload, Trash2, LogOut, Image, Loader2, CheckCircle, X, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('before-after');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const categories = [
    { value: 'before-after', label: 'Before & After' },
    { value: 'carpet', label: 'Carpet Cleaning' },
    { value: 'upholstery', label: 'Upholstery' },
    { value: 'rug', label: 'Area Rugs' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Check if already logged in (session storage)
    const loggedIn = sessionStorage.getItem('adminGalleryLoggedIn');
    if (loggedIn === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchImages();
    }
  }, [isLoggedIn, fetchImages]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, { password });
      if (response.data.success) {
        setIsLoggedIn(true);
        sessionStorage.setItem('adminGalleryLoggedIn', 'true');
      }
    } catch (error) {
      setLoginError('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminGalleryLoggedIn');
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setMessage({ type: '', text: '' });

    try {
      // Get signature from backend
      const sigResponse = await axios.get(`${API_URL}/api/cloudinary/signature?folder=carpex-gallery`);
      const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

      let successCount = 0;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('signature', signature);
        formData.append('timestamp', timestamp);
        formData.append('api_key', api_key);
        formData.append('folder', folder);

        // Upload to Cloudinary
        const uploadResponse = await axios.post(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          formData
        );

        // Save to database
        await axios.post(`${API_URL}/api/gallery`, {
          public_id: uploadResponse.data.public_id,
          url: uploadResponse.data.url,
          secure_url: uploadResponse.data.secure_url,
          category: selectedCategory,
          title: file.name.replace(/\.[^/.]+$/, '')
        });

        successCount++;
        setUploadProgress(Math.round((successCount / files.length) * 100));
      }

      setMessage({ type: 'success', text: `Successfully uploaded ${successCount} image(s)!` });
      fetchImages();
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Failed to upload images. Please try again.' });
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API_URL}/api/gallery/${imageId}`);
      setMessage({ type: 'success', text: 'Image deleted successfully!' });
      fetchImages();
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: 'Failed to delete image.' });
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="h-8 w-8 text-cyan-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Gallery</h1>
            <p className="text-slate-600 mt-2">Enter password to access photo uploads</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
            </div>
            
            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}

            <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              Login
            </Button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-6">
            <a href="/" className="text-cyan-600 hover:underline">← Back to website</a>
          </p>
        </div>
      </div>
    );
  }

  // Main Gallery Admin
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Gallery Admin</h1>
            <p className="text-sm text-slate-600">Upload and manage photos</p>
          </div>
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <X className="h-5 w-5" />}
              {message.text}
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })}>
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Upload New Photos</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Photos
              </label>
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                />
                <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  uploading 
                    ? 'border-slate-300 bg-slate-50' 
                    : 'border-cyan-300 hover:border-cyan-500 hover:bg-cyan-50'
                }`}>
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 text-cyan-600 animate-spin mb-2" />
                      <span className="text-slate-600">Uploading... {uploadProgress}%</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-cyan-600 mb-2" />
                      <span className="text-cyan-600 font-medium">Click to upload</span>
                      <span className="text-slate-500 text-sm mt-1">or drag and drop</span>
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Uploaded Photos ({images.length})
            </h2>
            <Button 
              onClick={fetchImages} 
              variant="outline" 
              size="sm"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 text-cyan-600 animate-spin" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <Image className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No photos uploaded yet</p>
              <p className="text-slate-400 text-sm">Upload your first photo above</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div key={image.id} className="relative group">
                  <img
                    src={image.secure_url}
                    alt={image.title || 'Gallery image'}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <button
                      onClick={() => handleDelete(image.id)}
                      className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <Badge className="bg-white/90 text-slate-700 text-xs">
                      {categories.find(c => c.value === image.category)?.label || image.category}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
